/** Tiny persisted client state (sessionStorage for the purchase in progress, localStorage for buyer details). */
import { useSyncExternalStore } from 'react';
import type { Tier } from '../../../src/domain/pricing';

export interface HoldState {
  sessionId: string;
  holdId: string;
  expiresAt: number;
  seats: { id: string; tier: Tier }[];
  checkoutKey?: string;
}
export interface Buyer {
  name: string;
  email: string;
  phone: string;
  sms: boolean;
}
export interface State {
  selection: Record<string, string[]>; // sessionId → seat ids
  hold: HoldState | null;
  admission: Record<string, { token: string; expiresAt: number }>;
  buyer: Buyer;
}

const SS = 'gise.state',
  LS = 'gise.buyer';
const load = (): State => {
  let s: Partial<State> = {};
  let buyer: Buyer = { name: '', email: '', phone: '', sms: true };
  try {
    s = JSON.parse(sessionStorage.getItem(SS) || '{}');
    buyer = { ...buyer, ...JSON.parse(localStorage.getItem(LS) || '{}') };
  } catch {
    /* ignore */
  }
  return { selection: s.selection || {}, hold: s.hold || null, admission: s.admission || {}, buyer };
};

let state: State = load();
const subs = new Set<() => void>();
export function setState(fn: (s: State) => State) {
  state = fn(state);
  try {
    sessionStorage.setItem(SS, JSON.stringify({ selection: state.selection, hold: state.hold, admission: state.admission }));
    localStorage.setItem(LS, JSON.stringify(state.buyer));
  } catch {
    /* ignore */
  }
  subs.forEach((f) => f());
}
export const getState = () => state;
export function useStore<T>(sel: (s: State) => T): T {
  return useSyncExternalStore(
    (f) => {
      subs.add(f);
      return () => subs.delete(f);
    },
    () => sel(state),
  );
}
